import React, { useRef, useState } from 'react';
import axios from 'axios';
import { Upload, Loader2, X, Image as ImageIcon } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Image uploader: reads file, uploads to backend, returns URL to parent.
 */
export default function ImageUploader({ value, onChange, label = 'Carica una foto', hint = 'JPG, PNG o WEBP fino a 5MB' }) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');

  const handleFile = async (file) => {
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      setError('File troppo grande (max 5MB)');
      return;
    }
    setError('');
    setUploading(true);
    try {
      // Convert to base64 and send
      const b64 = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });
      const res = await axios.post(`${API}/uploads`, {
        filename: file.name,
        content_type: file.type,
        data_url: b64,
      });
      onChange(res.data.url);
    } catch (e) {
      setError('Errore durante il caricamento');
    } finally {
      setUploading(false);
    }
  };

  const handleChange = (e) => handleFile(e.target.files?.[0]);

  return (
    <div>
      <input ref={inputRef} type="file" accept="image/*" className="hidden" onChange={handleChange} />
      {value ? (
        <div className="relative rounded-xl overflow-hidden border border-[color:var(--border)]" style={{ aspectRatio: '3 / 2' }}>
          <img src={value} alt="preview" className="w-full h-full object-cover" />
          <button onClick={() => onChange('')} className="absolute top-2 right-2 bg-black/60 text-white rounded-full p-1.5 hover:bg-black/80">
            <X size={14} />
          </button>
          <button onClick={() => inputRef.current?.click()} className="absolute bottom-2 right-2 bg-white/90 text-[color:var(--burgundy)] rounded-full text-xs px-3 py-1.5">
            Cambia
          </button>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          className="w-full border-2 border-dashed border-[color:var(--border)] hover:border-[color:var(--burgundy)] rounded-xl p-6 text-center transition bg-white/40"
        >
          {uploading ? (
            <div className="flex flex-col items-center gap-2 text-[color:var(--ink-soft)]">
              <Loader2 size={20} className="animate-spin" />
              <span className="text-sm">Caricamento…</span>
            </div>
          ) : (
            <div className="flex flex-col items-center gap-2">
              <div className="w-10 h-10 rounded-full bg-[color:var(--burgundy)]/10 flex items-center justify-center">
                <Upload size={18} className="text-[color:var(--burgundy)]" />
              </div>
              <div className="text-sm text-[color:var(--ink)] font-medium">{label}</div>
              <div className="text-xs text-[color:var(--ink-soft)]">{hint}</div>
            </div>
          )}
        </button>
      )}
      {error && <div className="mt-2 text-xs text-red-600">{error}</div>}
    </div>
  );
}
